import Link from "next/link";
import React, { useRef } from "react";
import Icon from "@mdi/react";
import { mdiLock } from "@mdi/js";
import AnalyticsService from "src/services/AnalyticsService";
import {
  useGame,
  getGameLeague,
  getGameLink,
  formatGamePickAsText,
  getBetTypeAlias,
  getGamePicks,
  useGameAccess,
} from "src/services/EventService";
import { MetaBetLink } from "src/services/OfferService";
import { useContainerQuery } from "src/services/UtilService";
import { renderRatingByValue } from "src/services/ValueDataService";
import GameHeader from "../GameHeader";
import { UpgradeButton } from "../UpgradeButton";
import styles from "./styles.scss";
import Spinner from "src/components/Spinner";
import { Pick } from "src/services/EventService";

const GamePick = ({ game, pick, showBetNow }: { game: any; pick: Pick; showBetNow: boolean }) => {
  const league = getGameLeague(game);
  const access = useGameAccess(game);

  const betTypeAlias = getBetTypeAlias({
    league,
    betType: pick.betType,
    period: pick.period,
  });

  const pickText = formatGamePickAsText({ game, pick });

  return (
    <div className="game-pick">
      <div className="pick-name">{betTypeAlias}</div>
      {access && <div className="pick-stars">{renderRatingByValue(pick.stars)}</div>}
      <div className="pick-data">
        {access ? (
          pickText
        ) : (
          <>
            <Icon style={{ marginRight: "5px", verticalAlign: "middle" }} path={mdiLock} size="18px" color="#004198" />
            <div className="pick-lock" />
          </>
        )}
      </div>
      {!showBetNow ? null : access ? (
        <MetaBetLink
          target="_blank"
          className="pick-bet-now"
          rel="noopener"
          onClick={() =>
            AnalyticsService.track("Bet Now", {
              url: window?.location?.href,
              sport: league.name,
              book: "MGM",
              component: "GamePicks",
              lineType: pick.betType,
            })
          }
          bookName="MGM"
          sportsdataGameId={game.sportsdataGameId}
        >
          Bet Now
        </MetaBetLink>
      ) : (
        <UpgradeButton className="upgrade-button" game={game} source="Game Picks">
          Unlock Best Bet
        </UpgradeButton>
      )}
    </div>
  );
};

const GamePicksList = ({ game, showBetNow }: { game: any; showBetNow: boolean }) => {
  const ref = useRef(null);
  useContainerQuery(ref, {
    small: 530,
    medium: 800,
    large: Infinity,
  });

  const picks = getGamePicks(game);

  return (
    <div ref={ref} className="game-picks-list">
      {picks.map((pick, idx) => (
        <GamePick key={idx} game={game} pick={pick} showBetNow={showBetNow} />
      ))}
    </div>
  );
};

const GamePicks = ({
  slug,
  ssr = false,
  accessOverride,
  showBetNow = true,
}: {
  slug: string;
  ssr?: boolean;
  accessOverride?: boolean;
  showBetNow?: boolean;
}) => {
  const { game } = useGame({ slug, ssr });
  const access = useGameAccess(game);

  if (!game) {
    return (
      <div className="game-picks loading">
        <Spinner size={32} color="#ebebeb" />
        <style jsx>{styles}</style>
      </div>
    );
  }

  // To make a game not free until user provides email
  if (typeof accessOverride === "boolean") {
    game.freeGame = accessOverride;
  }

  const gameLink = getGameLink(game);
  const league = getGameLeague(game);

  return (
    <div className="game-picks">
      <Link href={gameLink} legacyBehavior>
        <a className="game-header-as-link">
          <GameHeader game={game} />
        </a>
      </Link>
      {access ? (
        <Link href={gameLink} legacyBehavior>
          <a className="game-header-see-analysis">See full analysis</a>
        </Link>
      ) : (
        <div className="game-header-subscribe">
          Subscribe to Access All {league.name} Best Bets Including {game.awayTeam.lastName} vs.{" "}
          {game.homeTeam.lastName}{" "}
        </div>
      )}
      <GamePicksList game={game} showBetNow={showBetNow} />
      <style jsx>{styles}</style>
    </div>
  );
};

export default GamePicks;
